<template>
  <div class="edit-pricing-view">
    <!-- Back header -->
    <div class="view-header">
      <button class="back-btn" @click="exitPricingView">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Custom pricing</h5>
    </div>

    <div class="view-body">
      <!-- Enable toggle -->
      <div class="toggle-row mb-3">
        <el-switch active-color="#e14eca" v-model="enable_custom_pricing" />
        <label class="mb-0 ml-2" style="cursor: pointer" @click="enable_custom_pricing = !enable_custom_pricing">
          Enable custom pricing
        </label>
      </div>
      <info-hint v-if="!enable_custom_pricing" variant="primary">
        This slot will use the default pricing from the selected event.
      </info-hint>

      <template v-if="enable_custom_pricing">
        <!-- Pricing model -->
        <drawer-section title="Pricing model" icon="icon-coins">
          <div class="form-row">
            <div class="form-col">
              <base-input label="Pricing type">
                <el-select v-model="custom_pricing_type" class="select-primary" value-key="id" placeholder="Select pricing type">
                  <el-option v-for="(pt, i) in pricing_types" class="select-primary" :label="pt.name" :value="{ id: pt.id, name: pt.name }" :key="'PT' + i" />
                </el-select>
              </base-input>
            </div>
            <div class="form-col">
              <label class="text-white">Reservation style
                <el-popover trigger="hover" placement="top">
                  <div><div class="popover-body">Private bookings reserve the entire slot for one group.<br/>Public bookings allow multiple groups.</div></div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
              <base-input>
                <el-select v-model="is_public_booking" class="select-primary" value-key="id" placeholder="Reservation style">
                  <el-option v-for="(rs, i) in reservation_styles" class="select-primary" :label="rs.name" :value="{ id: rs.id, name: rs.name }" :key="'RS' + i" />
                </el-select>
              </base-input>
            </div>
          </div>

          <!-- Pricing options -->
          <div class="toggle-row mb-2">
            <el-switch active-color="#e14eca" v-model="cumulative_category_pricing" />
            <label class="mb-0 ml-2" style="cursor: pointer" @click="cumulative_category_pricing = !cumulative_category_pricing">
              Category pricing is cumulative
              <el-popover trigger="hover" placement="top">
                <div><div class="popover-body">Per-person rates adjust based on total group size across categories.</div></div>
                <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
              </el-popover>
            </label>
          </div>
          <div class="toggle-row mb-2">
            <el-switch active-color="#e14eca" v-model="hide_pricing_rate" />
            <label class="mb-0 ml-2" style="cursor: pointer" @click="hide_pricing_rate = !hide_pricing_rate">
              Hide pricing rate on booking site
            </label>
          </div>
          <div class="toggle-row mb-3">
            <el-switch active-color="#e14eca" v-model="limit_purchase_category" />
            <label class="mb-0 ml-2" style="cursor: pointer" @click="limit_purchase_category = !limit_purchase_category">
              Limit to one category per purchase
              <el-popover popper-class="otc-pop" trigger="hover" placement="top">
                <div><div class="popover-body">When enabled, customers can only purchase one pricing category at a time. When disabled, customers can combine multiple categories in a single purchase.</div></div>
                <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
              </el-popover>
            </label>
          </div>
        </drawer-section>

        <!-- Pricing categories -->
        <drawer-section title="Pricing categories" icon="icon-tag">
          <div class="toggle-row mb-3">
            <el-switch active-color="#e14eca" :value="enable_multiple_durations" @change="onDurationsToggle($event)" />
            <label class="mb-0 ml-2" style="cursor: pointer" @click="onDurationsToggle(!enable_multiple_durations)">
              Enable multiple durations
              <el-popover popper-class="otc-pop" trigger="hover" placement="top">
                <div><div class="popover-body">Offer this slot at several lengths. Each duration has its own pricing categories and tiers. Customers pick the duration after choosing a time.</div></div>
                <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
              </el-popover>
            </label>
          </div>
          <pricing-categories-editor :categories="activeCategories" :errors="pricingErrors" :set-key="activeDurationKey">
          <duration-pills v-if="enable_multiple_durations" class="mb-3"
                          :durations="durations" :value="active_duration_minutes"
                          @input="selectDuration" @add="addDuration" @remove="removeDuration" @set-default="setDefaultDuration" />
          </pricing-categories-editor>
        </drawer-section>

        <!-- Deposits -->
        <drawer-section title="Deposits" icon="icon-money-coins">
          <div class="toggle-row mb-2">
            <el-switch active-color="#e14eca" v-model="allow_deposit" />
            <label class="mb-0 ml-2" style="cursor: pointer" @click="allow_deposit = !allow_deposit">Allow deposit</label>
          </div>
          <div v-if="allow_deposit">
            <div class="toggle-row mb-2">
              <el-switch active-color="#e14eca" v-model="require_deposit" />
              <label class="mb-0 ml-2" style="cursor: pointer" @click="require_deposit = !require_deposit">Require only deposit amount during checkout</label>
            </div>
            <div class="form-row">
              <div class="form-col">
                <base-input label="Deposit type">
                  <el-select v-model="deposit_type" class="select-primary" value-key="id" placeholder="Value type">
                    <el-option v-for="(type, i) in valueTypes" class="select-primary" :label="type.name" :value="{ id: type.id, name: type.name }" :key="'VT' + i" />
                  </el-select>
                </base-input>
              </div>
              <div class="form-col">
                <base-input label="Deposit amount">
                  <div class="amount-input-group">
                    <span class="amount-prefix">{{ resolvedCurrencySymbol }}</span>
                    <el-input-number :controls="false" v-model="deposit_amount" :precision="2" :min="0" placeholder="0.00" />
                  </div>
                </base-input>
              </div>
            </div>
          </div>
        </drawer-section>
      </template>
    </div>

    <!-- Sticky footer -->
    <div class="view-footer">
      <base-button type="secondary" size="sm" @click="$emit('back')">Cancel</base-button>
      <base-button type="primary" size="sm" :disabled="saving" @click="savePricing">
        <i v-if="saving" class="el-icon-loading mr-1"></i>Save changes
      </base-button>
    </div>
  </div>
</template>

<script>
import { API_LOCATION } from '@/api-config';
import axios from 'axios';
import { splitCategoriesByDuration, flattenCategoriesByDuration } from '@/util/pricing';
import { Auth } from 'aws-amplify';
import swal from 'sweetalert2';
import currencies from '@/assets/currencies.json';
import {
  Popover,
  Select,
  Option,
  Switch,
  InputNumber
} from 'element-ui';

export default {
  name: 'EditPricingView',

  components: {
    [Popover.name]: Popover,
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
    [InputNumber.name]: InputNumber
  },

  props: {
    booking: { type: Object, required: true },
    companyId: { type: [String, Number], required: true },
    companyGroupId: { type: [String, Number], required: true },
    currencySymbol: { type: String, default: '$' },
    currency: { type: String, default: 'USD' },
    pricing: { type: Object, default: () => ({}) }
  },

  data() {
    return {
      saving: false,
      enable_custom_pricing: false,
      custom_pricing_type: { id: 1, name: 'Per person' },
      pricing_types: [
        { id: 1, name: 'Per person' },
        { id: 2, name: 'Flat rate' }
      ],
      is_public_booking: { id: 0, name: 'Private' },
      reservation_styles: [
        { id: 0, name: 'Private' },
        { id: 1, name: 'Public' }
      ],
      cumulative_category_pricing: false,
      hide_pricing_rate: false,
      limit_purchase_category: false,
      custom_pricing_categories: [
        { category: 'General', pricing: [{ min_players: 1, max_players: 10, price: 0 }], minimum_participants: 0, maximum_participants: undefined, default_participants: 1 }
      ],
      allow_deposit: false,
      require_deposit: false,
      deposit_type: { id: 0, name: 'Per booking' },
      deposit_amount: 0,
      valueTypes: [
        { id: 0, name: 'Per booking' },
        { id: 1, name: 'Per participant' }
      ],
      pricingErrors: {},
      pricingErrorsBySet: {},
      enable_multiple_durations: false,
      durations: [],
      active_duration_minutes: null
    };
  },

  computed: {
    activeDurationSet() {
      if (this.active_duration_minutes === null || this.active_duration_minutes === undefined) return null;
      return (this.durations || []).find(d => Number(d.duration_minutes) === Number(this.active_duration_minutes) && !d.is_default) || null;
    },
    activeCategories() {
      return this.activeDurationSet ? this.activeDurationSet.pricing_categories : this.custom_pricing_categories;
    },
    activeDurationKey() {
      return this.activeDurationSet ? String(this.activeDurationSet.duration_minutes) : 'default';
    },
    resolvedCurrencySymbol() {
      const info = currencies.currencies.find(c => c.code === this.currency);
      return info ? info.symbol : this.currencySymbol;
    }
  },

  created() {
    this.populateFromBooking();
  },

  methods: {
    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    populateFromBooking() {
      const p = this.pricing;
      if (!p) return;

      this.enable_custom_pricing = Boolean(p.is_custom_pricing_enabled);
      if (p.pricing_type_id) {
        this.custom_pricing_type = this.pricing_types.find(pt => pt.id === p.pricing_type_id) || { id: 1, name: 'Per person' };
      }
      if (p.is_public_booking !== undefined) {
        this.is_public_booking = this.reservation_styles.find(r => r.id === p.is_public_booking) || { id: 0, name: 'Private' };
      }
      this.cumulative_category_pricing = Boolean(p.cumulative_category_pricing);
      this.hide_pricing_rate = Boolean(p.hide_pricing_rate);
      this.limit_purchase_category = Boolean(p.limit_purchase_category);

      // Pricing tiers (already resolved by backend — slot tiers if custom, game tiers if not)
      if (p.tiers && p.tiers.length > 0) {
        this.custom_pricing_categories = this.getCategories(p.tiers);
      }
      //Multiple durations: split the tagged tiers into the default set and one set per duration
      const apiDurations = Array.isArray(p.durations) ? p.durations : [];
      this.enable_multiple_durations = Boolean(p.enable_multiple_durations);
      const split = splitCategoriesByDuration(this.custom_pricing_categories, apiDurations);
      this.custom_pricing_categories = split.defaultCategories.length > 0 ? split.defaultCategories : this.custom_pricing_categories;
      this.durations = apiDurations.map(d => {
        const isDefault = Number(d.is_default) === 1;
        return {
          duration_minutes: Number(d.duration_minutes),
          is_default: isDefault,
          position: Number(d.position) || 0,
          pricing_categories: isDefault ? [] : (split.byMinutes.get(Number(d.duration_minutes)) || [])
        };
      });
      this.active_duration_minutes = null;

      // Deposits
      this.allow_deposit = Boolean(p.allow_deposit);
      this.require_deposit = Boolean(p.require_deposit);
      if (p.deposit_type != null) {
        this.deposit_type = this.valueTypes.find(v => v.id === p.deposit_type) || { id: 0, name: 'Per booking' };
      }
      this.deposit_amount = p.deposit_amount || 0;
    },

    getCategories(items) {
      if (!items || items.length === 0) return [];
      const groupedArray = [];
      items.forEach(item => {
        const { category_id, category, minimum_participants, maximum_participants, default_participants, min_players, max_players, price } = item;
        let group = groupedArray.find(g => g.category_id === category_id);
        if (!group) {
          group = { category_id, category, maximum_participants, minimum_participants, default_participants, game_duration_id: item.game_duration_id === undefined ? null : item.game_duration_id, pricing: [] };
          groupedArray.push(group);
        }
        group.pricing.push({ min_players, max_players, price });
      });
      groupedArray.sort((a, b) => a.category_id - b.category_id);
      return groupedArray;
    },


    //Multiple durations: the default duration's categories stay in custom_pricing_categories;
    //every other duration owns its own array under durations[n].pricing_categories.
    selectDuration(minutes) {
      this.active_duration_minutes = minutes;
      this.pricingErrors = this.pricingErrorsBySet[this.activeDurationKey] || {};
    },
    ensureDefaultDuration() {
      if (!(this.durations || []).some(d => d.is_default)) {
        this.durations.unshift({ duration_minutes: 60, is_default: true, position: 0, pricing_categories: [] });
      }
    },
    onDurationsToggle(on) {
      this.enable_multiple_durations = !!on;
      if (on) this.ensureDefaultDuration();
      this.selectDuration(null);
    },
    addDuration(minutes) {
      this.ensureDefaultDuration();
      this.durations.push({
        duration_minutes: minutes,
        is_default: false,
        position: this.durations.length,
        pricing_categories: JSON.parse(JSON.stringify(this.custom_pricing_categories))
      });
      this.selectDuration(minutes);
    },
    removeDuration(minutes) {
      const idx = this.durations.findIndex(d => Number(d.duration_minutes) === Number(minutes) && !d.is_default);
      if (idx < 0) return;
      this.durations.splice(idx, 1);
      if (Number(this.active_duration_minutes) === Number(minutes)) this.selectDuration(null);
    },
    setDefaultDuration(minutes) {
      const next = this.durations.find(d => Number(d.duration_minutes) === Number(minutes) && !d.is_default);
      const current = this.durations.find(d => d.is_default);
      if (!next) return;
      const nextCategories = next.pricing_categories;
      next.pricing_categories = [];
      next.is_default = true;
      if (current) {
        current.is_default = false;
        current.pricing_categories = this.custom_pricing_categories;
      }
      this.custom_pricing_categories = nextCategories;
      this.pricingErrorsBySet = {};
      this.selectDuration(null);
    },





    validateCategorySet(categories) {
      const errors = {};
      for (let i = 0; i < categories.length; i++) {
        const cat = categories[i];
        const catErrs = {};

        if (!cat.category || !String(cat.category).trim()) {
          catErrs.category = 'Category cannot be empty';
        }

        const min = cat.minimum_participants;
        const max = cat.maximum_participants;
        const def = cat.default_participants;

        if (Number.isFinite(max) && Number.isFinite(min) && max < min) {
          catErrs.maximum_participants = 'Maximum participants cannot be less than minimum participants';
        } else if (Number.isFinite(max) && max <= 0) {
          catErrs.maximum_participants = 'Maximum participants cannot be 0 or less';
        }

        if (!Number.isFinite(min)) {
          catErrs.minimum_participants = 'Minimum participants cannot be empty';
        }

        if (!Number.isFinite(def)) {
          catErrs.default_participants = 'Default participants cannot be empty';
        } else if (Number.isFinite(min) && def < min) {
          catErrs.default_participants = 'Default participants cannot be less than minimum participants';
        } else if (Number.isFinite(max) && def > max) {
          catErrs.default_participants = 'Default participants cannot be greater than maximum participants';
        }

        const tierErrs = {};
        for (let j = 0; j < cat.pricing.length; j++) {
          const tier = cat.pricing[j];
          const te = {};

          if (!Number.isFinite(tier.price)) {
            te.price = 'Price cannot be empty';
          }

          if (!Number.isFinite(tier.max_players)) {
            te.max_players = 'Invalid value';
          } else if (Number.isFinite(tier.min_players) && tier.max_players < tier.min_players) {
            te.max_players = 'Invalid range';
            te.min_players = 'Invalid range';
          }

          if (!Number.isFinite(tier.min_players)) {
            te.min_players = 'Invalid value';
          } else if (Number.isFinite(tier.max_players) && tier.max_players < tier.min_players) {
            te.max_players = 'Invalid range';
            te.min_players = 'Invalid range';
          }

          if (Object.keys(te).length > 0) tierErrs[j] = te;
        }
        if (Object.keys(tierErrs).length > 0) catErrs.tiers = tierErrs;

        if (Object.keys(catErrs).length > 0) {
          errors[i] = catErrs;
        }
      }
      return errors;
    },

    validatePricing() {
      this.pricingErrorsBySet = {};
      this.pricingErrors = {};
      if (!this.enable_custom_pricing) return true;
      let isValid = true;
      let firstFailing;
      const sets = [{ key: 'default', categories: this.custom_pricing_categories, minutes: null }];
      if (this.enable_multiple_durations) {
        for (const d of (this.durations || [])) {
          if (!d.is_default) sets.push({ key: String(d.duration_minutes), categories: d.pricing_categories || [], minutes: Number(d.duration_minutes) });
        }
      }
      for (const set of sets) {
        const errs = this.validateCategorySet(set.categories);
        if (Object.keys(errs).length > 0 || (set.minutes !== null && set.categories.length === 0)) {
          isValid = false;
          if (firstFailing === undefined) firstFailing = set.minutes;
        }
        this.pricingErrorsBySet[set.key] = errs;
      }
      if (!isValid && firstFailing !== undefined) this.active_duration_minutes = firstFailing;
      this.pricingErrors = this.pricingErrorsBySet[this.activeDurationKey] || {};

      if (!isValid) {
        this.$notify({
          type: 'danger',
          message: 'Fix the highlighted pricing errors before saving',
          icon: 'tim-icons icon-alert-circle-exc',
          timeout: 4000
        });
      }

      return isValid;
    },

    exitPricingView() {
      if (!this.validatePricing()) return;
      this.$emit('back');
    },

    async savePricing() {
      if (!this.validatePricing()) return;

      this.saving = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings/${this.booking.id}/v2`;

        const data = {
          is_custom_pricing_enabled: this.enable_custom_pricing
        };

        if (this.enable_custom_pricing) {
          data.pricing_type_id = this.custom_pricing_type?.id || 1;
          data.is_public_booking = this.is_public_booking?.id || 0;
          data.cumulative_category_pricing = this.cumulative_category_pricing;
          data.hide_pricing_rate = this.hide_pricing_rate;
          data.limit_purchase_category = this.limit_purchase_category;
          data.custom_pricing = flattenCategoriesByDuration(this.custom_pricing_categories, this.enable_multiple_durations ? this.durations : []).map(cat => ({
            ...cat,
            maximum_participants: cat.maximum_participants ?? null
          }));
          data.enable_multiple_durations = this.enable_multiple_durations ? 1 : 0;
          data.durations = this.enable_multiple_durations ? this.durations.map(d => ({ duration_minutes: Number(d.duration_minutes), is_default: d.is_default ? 1 : 0, position: Number(d.position) || 0 })) : [];
          data.allow_deposit = this.allow_deposit;
          data.require_deposit = this.require_deposit;
          data.deposit_type = this.deposit_type?.id ?? 0;
          data.deposit_amount = this.deposit_amount || 0;
        }

        await axios.put(url, data, config);

        this.$notify({
          type: 'success',
          message: 'Pricing has been updated',
          icon: 'tim-icons icon-check-2'
        });
        this.$emit('updated');
      } catch (error) {
        console.error('Failed to update pricing:', error);
        if (error.response?.status === 401) {
          this.$router.push('/forbidden');
        } else {
          swal('Error', error.response?.data || 'Failed to update pricing', 'error');
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.edit-pricing-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.view-header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  gap: 12px;
}

.back-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
}

.view-title {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.view-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.view-footer {
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.section-label {
  text-transform: uppercase;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
}

.form-row {
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
}

.form-col {
  flex: 1;
}

.toggle-row {
  display: flex;
  align-items: center;

  label {
    color: #fff;
    font-size: 13px;
  }
}

// Pricing categories




// Tiers section - indented under category







// Participant requirements - visually separated

// Amount input group (currency prefix pattern)
.amount-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);
  height: 32px;

  &:focus-within { border-color: #e14eca; }
}

.amount-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  background: rgba(225, 78, 202, 0.15);
  font-weight: 600;
  font-size: 12px;
  min-width: 28px;
  color: #e14eca;
}






</style>

<style lang="scss">
// Constrain el-input-number to fit within drawer
.edit-pricing-view {
  .el-input-number--small {
    width: 100%;
  }


  .amount-input-group .el-input-number .el-input__inner {
    text-align: left;
    color: white;
    background: transparent;
    border: none;
    height: 30px;
    line-height: 30px;
    font-size: 13px;
  }
}

// Light mode overrides
.white-content {
  .edit-pricing-view {
    .view-header {
      border-bottom-color: #e9ecef;
    }

    .view-title {
      color: #344675;
    }

    .back-btn {
      background: #f0f2f5;
      border-color: #d1d5db;
      color: #525f7f;

      &:hover {
        background: #e2e6ea;
        color: #344675;
      }
    }

    .view-footer {
      border-top-color: #e9ecef;
    }

    .section-label {
      color: #525f7f;
    }

    .toggle-row label {
      color: #525f7f;
    }













    .amount-input-group {
      background: #ffffff;
      border-color: rgba(225, 78, 202, 0.3);
    }

    .text-white {
      color: #344675 !important;
    }
  }
}
</style>
